import * as React from 'react';
import { MAX_ENTRY_SECONDS, normalizeDuration, parseDuration } from '@cfl/shared';
import { DURATION_LIST_ID, DurationPresets } from './duration-presets';

import {
  Alert, AlertDescription, Button, Checkbox, Combobox, Dialog, DialogContent,
  DialogFooter, DialogHeader, DialogTitle, Input, Label,
} from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { fitsProject, useActivities, useProjects } from '@/lib/catalog-api';
import { useBulkInsert, type BulkResult } from '@/lib/timesheet-api';

/**
 * Fill a date range with the same entry — the equivalent of the legacy system's
 * PeriodInsertBundle, and how month-end catch-up actually gets done.
 *
 * Locked days are skipped rather than failing the request, and the result says how
 * many. Refusing the whole range because one day is closed would make the tool
 * useless in exactly the week people reach for it.
 */
export function BulkDialog({
  open,
  onOpenChange,
  defaultFrom,
  defaultTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultFrom: string;
  defaultTo: string;
}) {
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const bulk = useBulkInsert();

  const [from, setFrom] = React.useState(defaultFrom);
  const [to, setTo] = React.useState(defaultTo);
  const [projectId, setProjectId] = React.useState('');
  const [activityId, setActivityId] = React.useState('');
  const [duration, setDuration] = React.useState('8:00');
  const [includeWeekends, setIncludeWeekends] = React.useState(false);
  // All seven ticked, so the default fill is exactly what it was before this
  // existed. Unticking NARROWS - filling Mon/Wed/Fri is one pass, not three.
  const [weekdays, setWeekdays] = React.useState<number[]>([1, 2, 3, 4, 5, 6, 0]);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<BulkResult | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFrom(defaultFrom);
    setTo(defaultTo);
    setError(null);
    setResult(null);
  }, [open, defaultFrom, defaultTo]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const seconds = parseDuration(duration);
    if (seconds === null) return setError('Enter a duration like 8:00 or 7.5.');
    if (seconds > MAX_ENTRY_SECONDS) return setError('A day cannot be longer than 24 hours.');
    if (!projectId || !activityId) return setError('Choose a project and an activity.');
    if (!from || !to) return setError('Choose both a From and a To date.');
    if (to < from) return setError(`To (${to}) is before From (${from}). Swap them or pick again.`);

    try {
      setResult(
        await bulk.mutateAsync({
          from,
          to,
          projectId,
          activityId,
          durationSeconds: seconds,
          includeWeekends,
          weekdays,
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not fill that range.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fill a date range</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Project</Label>
            <Combobox
              value={projectId || null}
              onChange={setProjectId}
              options={(projects ?? []).map((p) => ({
                value: p.id, label: p.name, description: p.customer.name,
              }))}
              placeholder="Choose a project"
              searchPlaceholder="Search projects"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Activity</Label>
            <Combobox
              value={activityId || null}
              onChange={setActivityId}
              options={(activities ?? [])
                .filter((a) => fitsProject(a, projectId, projects))
                .map((a) => ({ value: a.id, label: a.name }))}
              placeholder="Choose an activity"
              searchPlaceholder="Search activities"
            />
          </div>

          <div className="space-y-1.5">
            <DurationPresets />
            <Label htmlFor="bulk-duration">Hours per day</Label>
            <Input
              id="bulk-duration"
              list={DURATION_LIST_ID}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              onBlur={(e) => setDuration(normalizeDuration(e.target.value))}
              placeholder="8:00"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Days to fill</Label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map(({ day, label }) => {
                const on = weekdays.includes(day);
                return (
                  <Hint label={`Fill ${label}s`}><button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setWeekdays((prev) =>
                        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
                      )
                    }
                    className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                      on
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-input text-muted-foreground'
                    }`}
                  >
                    {label}
                  </button></Hint>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Public holidays and approved leave are always skipped, whichever days are on.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={includeWeekends}
              onCheckedChange={(v) => setIncludeWeekends(v === true)}
            />
            Include days the contract does not work
          </label>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert>
              <AlertDescription className="space-y-1 text-sm leading-relaxed">
                <div>
                  Added <strong>{result.created}</strong>{' '}
                  {result.created === 1 ? 'entry' : 'entries'}.
                </div>
                {result.skippedNotChosen > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedNotChosen} {result.skippedNotChosen === 1 ? 'day' : 'days'} not
                    among the days you chose.
                  </div>
                )}
                {result.skippedNonWorking > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedNonWorking} non-working{' '}
                    {result.skippedNonWorking === 1 ? 'day' : 'days'} skipped.
                  </div>
                )}
                {/* Named separately from a weekend: a holiday already carries the
                    hours the nightly fill booked, and a fill on top would double
                    them. Same for leave that was approved. */}
                {result.skippedHoliday > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedHoliday} public {result.skippedHoliday === 1 ? 'holiday' : 'holidays'}{' '}
                    skipped, starting {result.firstHoliday}.
                  </div>
                )}
                {result.skippedAbsence > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedAbsence} {result.skippedAbsence === 1 ? 'day' : 'days'} of approved
                    leave skipped.
                  </div>
                )}
                {result.skippedOutsideProject > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedOutsideProject}{' '}
                    {result.skippedOutsideProject === 1 ? 'day' : 'days'} outside the project&rsquo;s
                    own dates skipped.
                  </div>
                )}
                {result.skippedLocked > 0 && (
                  <div className="text-muted-foreground">
                    {result.skippedLocked} already-submitted{' '}
                    {result.skippedLocked === 1 ? 'day' : 'days'} skipped, starting{' '}
                    {result.firstLockedDay}.
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            <Button hint={"Book the same time on every chosen day in the range"} type="submit" disabled={bulk.isPending}>
              {bulk.isPending ? 'Filling…' : 'Fill range'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Monday first, as every week in this system is. `day` is `Date.getUTCDay()`. */
const WEEKDAYS = [
  { day: 1, label: 'Mon' },
  { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' },
  { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' },
  { day: 6, label: 'Sat' },
  { day: 0, label: 'Sun' },
];
